import React, { useState } from 'react';
import { Receipt } from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { InvoiceManagerSlider } from '@components/organisms/InvoiceManagerSlider';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import {
  Activity,
  PendingItemSnapshotEntry,
} from '@models/activity.model';
import { formatForDisplay } from '@utils/formatDate';

interface Props {
  activities: Activity[];
  isLoading?: boolean;
  onOpenTask?: (taskId: string) => void;
}

// Same roles allowed to invoice as the dashboard INVOICE_PENDING activity card.
const INVOICING_ROLES: Array<UserRoles> = [
  UserRoles.OWNER,
  UserRoles.ADMIN,
  UserRoles.PROJECT_MANAGER,
  UserRoles.SUPER_ADMIN,
];

const countByType = (
  snapshot: PendingItemSnapshotEntry[],
  type: 'MATERIAL' | 'EQUIPMENT',
) => snapshot.filter((s) => s.itemType === type).length;

/**
 * Tabular view of unresolved INVOICE_PENDING activities (completed tasks with unbilled
 * materials/equipment) for the Invoices page "Pending Invoices" tab. Shows the same
 * information as the dashboard activity feed card and reuses the per-project
 * InvoiceManagerSlider to create the invoice.
 */
export const PendingInvoicesTable: React.FC<Props> = ({
  activities,
  isLoading,
  onOpenTask,
}) => {
  const userRole = useAppSelector((state: RootState) => state.user.role);
  const canInvoice = Boolean(
    userRole && INVOICING_ROLES.includes(userRole as UserRoles),
  );

  const [sliderProject, setSliderProject] = useState<{
    projectId: string;
    name?: string;
    email?: string;
  } | null>(null);

  if (isLoading && activities.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-500">
        Loading pending invoices…
      </div>
    );
  }

  if (!isLoading && activities.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <p className="text-sm text-gray-600">No pending invoices. Completed tasks with unbilled items will appear here.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Task</th>
                <th className="px-4 py-3 text-left">Project</th>
                <th className="px-4 py-3 text-left">Pending to invoice</th>
                <th className="px-4 py-3 text-left">Completed</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activities.map((activity) => {
                const snapshot = activity.pendingItemsSnapshot ?? [];
                const equipmentCount = countByType(snapshot, 'EQUIPMENT');
                const materialCount = countByType(snapshot, 'MATERIAL');
                const taskId = activity.taskId || activity.task?.taskId;
                const taskName = activity.task?.name || activity.name || 'Task';
                const canOpenTask = Boolean(taskId && onOpenTask);
                const projectId = activity.project?.projectId;

                return (
                  <tr key={activity.activityId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (taskId && onOpenTask) onOpenTask(taskId);
                        }}
                        disabled={!canOpenTask}
                        className="flex items-center gap-2 text-left font-medium text-blue-800 disabled:text-gray-700 disabled:cursor-default hover:underline disabled:no-underline">
                        <Receipt size={16} />
                        {taskName}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-700 capitalize">
                      {activity.project?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {equipmentCount + materialCount > 0 ? (
                        <span>
                          {equipmentCount > 0 &&
                            `${equipmentCount} Equipment${equipmentCount === 1 ? '' : 's'}`}
                          {equipmentCount > 0 && materialCount > 0 && ', '}
                          {materialCount > 0 &&
                            `${materialCount} Material${materialCount === 1 ? '' : 's'}`}
                        </span>
                      ) : (
                        <span className="text-gray-400">No pending items</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {activity.createdAt ? formatForDisplay(activity.createdAt) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        {canInvoice ? (
                          <Button
                            variant="primary"
                            size="md"
                            leftIcon={<Receipt size={14} />}
                            onClick={() =>
                              projectId &&
                              setSliderProject({
                                projectId,
                                name: activity.project?.name,
                                email: activity.project?.client?.email,
                              })
                            }
                            disabled={!projectId}>
                            Create Invoice
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-500 italic">
                            Awaiting invoicing by manager
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {sliderProject && (
        <InvoiceManagerSlider
          isOpen={true}
          projectId={sliderProject.projectId}
          projectName={sliderProject.name}
          defaultRecipientEmail={sliderProject.email}
          onClose={() => setSliderProject(null)}
        />
      )}
    </>
  );
};
